// Libraries
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'lodash';
// Components
import { ProgressButton } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts';
import { AnswersContainer } from './Parts';
// Data
import { DEFAULT_MS_TIMEOUT_TO_ADVANCE, HINT_ICON_ID } from './constants';

const RadioGroupV2QA = ({ answers, onChange, questionsPerColumn, ...rest }) => {
  const [showButton, setShowButton] = useState(false);
  const [buttonIsDisabled, setButtonIsDisabled] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState({ value: null, stop: false });
  const [elementClicked, setElementClicked] = useState(null);

  const showButtonElements = () => setShowButton(true);
  const hideButtonElements = () => setShowButton(false);

  const shouldShowContinueButton = showButton && selectedAnswer.stop;

  const submitRadioSelection = {
    valuesToSubmit: null,
    stop: null,
    submit() {
      if (this.valuesToSubmit && !this.stop) onChange(this.valuesToSubmit);
      this.timeoutId = undefined;
    },
    cancel() {
      clearTimeout(this.timeoutId);
    },
    trySubmit() {
      if (typeof this.timeoutId === 'number') this.cancel();
      this.timeoutId = setTimeout(() => {
        this.submit();
      }, DEFAULT_MS_TIMEOUT_TO_ADVANCE);
    },
  }; // This handles submitting answers that don't require any user input/interaction

  const handleRadioClick = useRef(submitRadioSelection);

  const handleButtonClick = () => {
    /*
      This handles submitting answers that require user input/interaction
      If selectedAnswer.static, the radio value should be submitted
      If !selectedAnswer.static the radio value and additional text should be submitted
    */
    if (!selectedAnswer) return null;
    if (selectedAnswer.static) return onChange(selectedAnswer.value);
    return onChange(omit(selectedAnswer, ['stop', 'static']));
  };

  const shouldDisableButton = () => {
    const isTextInput = !selectedAnswer.static;
    if (isTextInput) {
      if (typeof selectedAnswer.value === 'number') return true;
      if (typeof selectedAnswer.value === 'string' && !selectedAnswer.value.length) return true;
    }
    return false;
  };

  useEffect(() => {
    setButtonIsDisabled(shouldDisableButton());
    handleRadioClick.current.valuesToSubmit = selectedAnswer.value;
    handleRadioClick.current.stop = selectedAnswer.stop;
    if (selectedAnswer.stop) showButtonElements();
    if (!selectedAnswer.stop) hideButtonElements();
    handleRadioClick.current.trySubmit();
  }, [selectedAnswer]);

  useEffect(() => {
    // Disable the continue button if not a freeform input, the hint icon was clicked, but the radio wasn't checked.
    if (!elementClicked || !selectedAnswer.static) return;
    if (elementClicked.getAttribute(HINT_ICON_ID)) {
      const input = document.querySelector(`#answer${selectedAnswer.value}`);
      setButtonIsDisabled(input && !input.checked);
      return;
    }
    setButtonIsDisabled(false);
  }, [elementClicked, selectedAnswer]);

  return (
    <>
      <AnswersContainer
        answers={answers}
        questionsPerColumn={questionsPerColumn}
        setElementClicked={setElementClicked}
        setSelectedAnswer={setSelectedAnswer}
        {...rest}
      />
      {shouldShowContinueButton && (
        <ProgressButton
          buttonText="Continue"
          onClick={handleButtonClick}
          isDisabled={buttonIsDisabled}
        />
      )}
    </>
  );
};

RadioGroupV2QA.propTypes = {
  answers: PropTypes.array,
  onChange: PropTypes.func,
  questionsPerColumn: PropTypes.number,
};

export default RadioGroupV2QA;

/*
  RadioGroupV2QA: A container meant to be used with RadioV2QA.

  Example:

  <RadioGroupV2QA onChange={onChange} name={`question${questionJS.id}`} answers={answers} />
*/
